import { createStore } from '@oneappexchange/lwc-wired';
import { RouterState } from '@oneappexchange/lwc-wired-router';
import { DeepNonNullable } from 'ts-essentials';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { getPluginLastModifiedDate, getPluginPublishedDate } from '~/modules/utilities/plugin_utils';
import { isPluginListing, isSkillListing, selectExtensionListing } from '~/modules/utilities/listing_utils';
import { getSkillLastModifiedDate, getSkillPublishedDate } from '~/modules/utilities/skill_utils';
import {
    ContentRecords,
    ContentState,
    contentStore,
    fetchSponsoredFeed,
    selectAmpCampaign,
    selectSponsoredFeed,
} from './contentStore';
import { ListingState, listingStore, selectListing } from './listingStore';
import { routerStore, selectQueryParams } from './routerStore';
import { UserState, userStore } from './userStore';

export interface AmpCampaign {
    campaign_name?: string;
    placement_id?: string;
    planned_end_date?: string;
    planned_start_date?: string;
}
export interface AnalyticsData {
    oafId?: string;
    external_id?: string;
    title?: string;
    listingRecordType?: string;
    pricing?: string;
    listingCategoryName?: string;
    recommended?: boolean;
    publisher?: string;
    sponsoredCampaign?: AmpCampaign;
    marketplaceLastUpdated?: string;
    marketplaceListedOn?: string;
}
export interface AnalyticsState extends State {
    lastPageViewUrl?: string;
    userLoading?: boolean;
    ampDataAvailable: boolean;
    listingAvailable: boolean;
    onListingDetailPage: boolean;
    sponsoredListing: boolean;
    sendPageView: boolean;
    listingId: string;
    pageViewSent: boolean;
    data: Record<string, AnalyticsData>;
}

// @ts-expect-error * Automated Type Error Fix
const initialState = {
    ampDataAvailable: false,
    listingAvailable: false,
    sponsoredListing: false,
    onListingDetailPage: false,
    sendPageView: false,
    listingId: undefined,
    userLoading: true,
    pageViewSent: false,
    data: {},
} as AnalyticsState;

type SlicedRoute = {
    url: string;
    queryString: string;
    params: { [key: string]: string } | null;
};

// https://github.com/ts-essentials/ts-essentials/blob/852a08fe6fd20781333cb29a1d0b6515cfa49d20/lib/deep-non-nullable/README.md
type ListingDetailRoute = DeepNonNullable<SlicedRoute>;

const isListingDetailPage = (routerState: SlicedRoute): routerState is ListingDetailRoute => {
    const listingId = routerState.params?.listingId;
    return Routes.LISTING === routerState.url && !!listingId;
};

const isSponsoredListing = (channel: string, placement: string) => {
    return channel && channel === 'featured' && placement;
};

const getCurrentUrl = (routerState: SlicedRoute) => {
    if (isListingDetailPage(routerState)) {
        // Dedup page views on listingId alone. Tab switches on the v2 listing page
        // are client-side (SPA) but keep the same listingId, so they intentionally do
        // NOT register as new page views here — v2 matches v1's page_view volume. Tab
        // engagement is tracked separately as a click event (ANALYTICS_CLICK,
        // elementType 'tab' → Data Cloud `listingTabSwitch` + GTM `custEv_appEngagement`),
        // so no per-tab page view is needed.
        return `${routerState.url}?listingId=${routerState.params.listingId}`;
    }
    return `${routerState.url}?${routerState.queryString}`;
};

const isListingReady = (state: AnalyticsState): boolean => {
    // Is TRUE when:
    // - Not on Listing Details
    // - On Listing Details and Listing is in listingStore (fetched or pre-existing)
    return !state.onListingDetailPage || (state.onListingDetailPage && state.listingAvailable);
};

const isAmpReady = (state: AnalyticsState): boolean => {
    // Is TRUE when:
    // - Not Sponsored
    // - Sponsored and campaign information on contentStore (fetched or pre-existing)
    return !state.sponsoredListing || (state.sponsoredListing && state.ampDataAvailable);
};

const shouldSendPageView = (state: AnalyticsState): boolean => {
    return !state.userLoading && isListingReady(state) && isAmpReady(state);
};

const loadListingDetail = (listing: string): AnalyticsData => {
    const { channel } = selectQueryParams(routerStore.getState());
    const listingDetail = selectListing(listingStore.getState(), listing);
    const pricing = listingDetail?.pricing?.price_model_type;
    // @ts-expect-error * Automated Type Error Fix
    const forceListing = selectExtensionListing(listingDetail);
    const listingCategories = forceListing?.listingCategories;
    const listingRecordType = forceListing?.recordType;
    const publisher = listingDetail?.publisher?.name;
    const marketplaceListing = listingDetail ?? undefined;
    const marketplaceLastUpdated = isSkillListing(marketplaceListing)
        ? getSkillLastModifiedDate(listingDetail)
        : isPluginListing(marketplaceListing)
          ? getPluginLastModifiedDate(listingDetail)
          : undefined;
    const marketplaceListedOn = isSkillListing(marketplaceListing)
        ? getSkillPublishedDate(listingDetail)
        : isPluginListing(marketplaceListing)
          ? getPluginPublishedDate(listingDetail)
          : undefined;

    return {
        listingCategoryName: (listingCategories?.length && listingCategories[0]) || '',
        oafId: listingDetail?.oafId,
        external_id: listingDetail?.external_id,
        pricing,
        listingRecordType,
        title: listingDetail?.name,
        recommended: channel === 'recommended',
        publisher,
        ...(marketplaceLastUpdated ? { marketplaceLastUpdated } : {}),
        ...(marketplaceListedOn ? { marketplaceListedOn } : {}),
    };
};

const loadAmpData = () => {
    const { listingId, placement } = selectQueryParams(routerStore.getState());
    return selectAmpCampaign(contentStore.getState(), listingId, placement);
};

export const analyticsStore = createStore(
    initialState,
    (set: SetState<AnalyticsState>, get: GetState<AnalyticsState>) => ({
        setLastPageViewUrl(url: string) {
            set(function setCurrentUrl(draft) {
                draft.lastPageViewUrl = url;
            });
        },
        setIsListingDetailPage(value: boolean) {
            set(function setIsListingDetailPage(draft) {
                draft.onListingDetailPage = value;
            });
        },
        setListingAvailable() {
            set(function setListingAvailable(draft) {
                draft.listingAvailable = true;
                const currentData = draft.data[draft.listingId];
                draft.data[draft.listingId] = { ...currentData, ...loadListingDetail(draft.listingId) };
            });
        },
        setUserState(isLoading: boolean) {
            set(function setUserState(draft) {
                draft.userLoading = isLoading;
            });
        },
        setAmpDataAvailable() {
            set(function setAmpDataAvailable(draft) {
                draft.ampDataAvailable = true;
                const currentData = draft.data[draft.listingId];
                draft.data[draft.listingId] = { ...currentData, ...loadAmpData() };
            });
        },
        setListing(listing: string) {
            set(function setListing(draft) {
                draft.listingId = listing;
            });
        },
        updateSendPageView() {
            const updatePageView = shouldSendPageView(get());
            if (updatePageView && !get().sendPageView) {
                set(function sendPageView(draft) {
                    draft.sendPageView = true;
                });
            }
        },
        pageViewSent() {
            set(function pageViewSent(draft) {
                draft.pageViewSent = true;
            });
        },
        reset() {
            set(function resetStore() {
                return { ...initialState, userLoading: get().userLoading, data: get().data };
            });
        },
    }),
    'analyticsStore',
    IS_DEVELOPMENT,
);

const loadListingData = () => {
    analyticsStore.setListingAvailable();
    analyticsStore.updateSendPageView();
};

const setupListingDetailPage = (routerState: ListingDetailRoute) => {
    const { listingId, channel, placement } = routerState.params;
    analyticsStore.setListing(listingId);
    analyticsStore.setIsListingDetailPage(true);

    const hasListing = !!selectListing(listingStore.getState(), listingId);
    const isSponsored = !!isSponsoredListing(channel, placement);
    const ampReady = !!selectSponsoredFeed(contentStore.getState());
    if (ampReady) {
        analyticsStore.setAmpDataAvailable();
    }
    analyticsStore.setState({
        listingAvailable: hasListing,
        sponsoredListing: isSponsored,
        ampDataAvailable: ampReady,
    });

    if (isSponsored && !ampReady) {
        fetchSponsoredFeed();
    }
    if (hasListing) {
        loadListingData();
    }
};

const hasUrlChanged = (routerState: SlicedRoute): boolean => {
    const currentUrl = getCurrentUrl(routerState);
    return analyticsStore.getState().lastPageViewUrl !== currentUrl;
};

userStore.subscribe(
    (userLoading: boolean) => {
        if (!userLoading) {
            analyticsStore.setUserState(userLoading);
            analyticsStore.updateSendPageView();
        }
    },
    (state: UserState) => state.isLoading,
);

routerStore.subscribe(
    (sliced: SlicedRoute) => {
        if (hasUrlChanged(sliced)) {
            analyticsStore.reset();
            analyticsStore.setLastPageViewUrl(getCurrentUrl(sliced));
            if (isListingDetailPage(sliced)) {
                setupListingDetailPage(sliced);
            }
            analyticsStore.updateSendPageView();
        }
    },
    (state: RouterState) => ({ url: state.url, queryString: state.queryString, params: state.params }),
);

contentStore.subscribe(
    (sponsored: ContentRecords) => {
        if (sponsored) {
            analyticsStore.setAmpDataAvailable();
            analyticsStore.updateSendPageView();
        }
    },
    (state: ContentState) => state.feeds?.sponsored,
);

listingStore.subscribe(
    (_) => {
        loadListingData();
    },
    (state: ListingState) => {
        return state.listings.get(analyticsStore.getState().listingId);
    },
);

export const selectListingData = (listing: string = analyticsStore.getState().listingId): AnalyticsData => {
    return analyticsStore.getState().data[listing];
};
